import { defineDspModule, type DspModuleInstance, type DspProcessBlock } from '../../dsp-module-contract/src/index.js';
import { SeedTree } from '../../mod-035/src/index.js';

export interface SequencerMeter {
  readonly numerator: number;
  readonly denominator: number;
}

export interface SequencerEvent {
  readonly id: string;
  readonly tick: number;
  readonly durationTicks: number;
  readonly value: number;
  readonly probability?: number;
  readonly chokeGroup?: number;
}

export type TransportCommand =
  | { readonly type: 'start'; readonly frame: number; readonly tick?: number }
  | { readonly type: 'stop'; readonly frame: number }
  | { readonly type: 'seek'; readonly frame: number; readonly tick: number }
  | { readonly type: 'tempo'; readonly frame: number; readonly bpm: number };

export interface EventSequencerOptions {
  readonly sampleRate: number;
  readonly events: readonly SequencerEvent[];
  readonly bpm?: number;
  readonly meter?: SequencerMeter;
  readonly ppq?: number;
  readonly loopBars?: number;
  readonly swing?: number;
  readonly seed?: number;
  readonly globalProbability?: number;
  readonly playing?: boolean;
  readonly maxEventsPerBlock?: number;
}

export interface SequencerOutputs {
  readonly trigger: Float32Array;
  readonly gate: Float32Array;
  readonly value: Float32Array;
  readonly choke: Float32Array;
}

interface PreparedEvent extends SequencerEvent {
  readonly probability: number;
  readonly probabilitySeed: number;
  readonly swungTick: number;
}

interface QueuedCommand {
  readonly command: TransportCommand;
  readonly order: number;
}

const EVENT_ID = /^[a-z][a-z0-9-]*$/;
const EPSILON = 1e-9;
const UINT32_MAX = 0xffff_ffff;
const UINT32_RANGE = 0x1_0000_0000;

const assertFiniteRange = (label: string, value: number, minimum: number, maximum: number): void => {
  if (!Number.isFinite(value) || value < minimum || value > maximum) {
    throw new RangeError(`${label} must be in [${minimum}, ${maximum}]`);
  }
};

const assertIntegerRange = (label: string, value: number, minimum: number, maximum: number): void => {
  if (!Number.isInteger(value) || value < minimum || value > maximum) {
    throw new RangeError(`${label} must be an integer in [${minimum}, ${maximum}]`);
  }
};

const assertBpm = (bpm: number): void => assertFiniteRange('bpm', bpm, 1, 1_000);
const assertProbability = (label: string, value: number): void => assertFiniteRange(label, value, 0, 1);
const assertSeed = (seed: number): void => assertIntegerRange('seed', seed, 0, UINT32_MAX);
const assertTick = (label: string, tick: number): void => {
  if (!Number.isSafeInteger(tick) || tick < 0) throw new RangeError(`${label} must be a non-negative safe integer`);
};

/**
 * Sample-frame sequencer core. It owns only transport state; the host owns
 * caller buffers, graph wiring and voice allocation.
 */
export class EventSequencer {
  readonly sampleRate: number;
  readonly ppq: number;
  readonly meter: SequencerMeter;
  readonly loopBars: number;
  readonly loopTicks: number;
  readonly maxEventsPerBlock: number;

  private readonly sourceEvents: readonly SequencerEvent[];
  private readonly initialPlaying: boolean;
  private preparedEvents: readonly PreparedEvent[] = [];
  private bpm: number;
  private baseBpm: number;
  private swing: number;
  private seed: number;
  private globalProbability: number;
  private frame = 0;
  private tick = 0;
  private playing: boolean;
  private currentValue = 0;
  private readonly activeEnds: number[] = [];
  private readonly commands: QueuedCommand[] = [];
  private commandIndex = 0;
  private nextCommandOrder = 0;

  constructor(options: EventSequencerOptions) {
    assertFiniteRange('sampleRate', options.sampleRate, 1, 768_000);
    this.sampleRate = options.sampleRate;
    this.ppq = options.ppq ?? 960;
    assertIntegerRange('ppq', this.ppq, 1, 96_000);
    const meter = options.meter ?? { numerator: 4, denominator: 4 };
    assertIntegerRange('meter numerator', meter.numerator, 1, 64);
    assertIntegerRange('meter denominator', meter.denominator, 1, 64);
    if ((meter.denominator & (meter.denominator - 1)) !== 0) throw new RangeError('meter denominator must be a power of two');
    const ticksPerBar = meter.numerator * this.ppq * 4 / meter.denominator;
    if (!Number.isSafeInteger(ticksPerBar)) throw new RangeError('meter and ppq must produce an integer bar length');
    this.meter = Object.freeze({ ...meter });
    this.loopBars = options.loopBars ?? 1;
    assertIntegerRange('loopBars', this.loopBars, 1, 1_024);
    this.loopTicks = ticksPerBar * this.loopBars;
    if (!Number.isSafeInteger(this.loopTicks)) throw new RangeError('loop length exceeds safe integer range');
    this.maxEventsPerBlock = options.maxEventsPerBlock ?? 256;
    assertIntegerRange('maxEventsPerBlock', this.maxEventsPerBlock, 1, 256);

    this.baseBpm = options.bpm ?? 120;
    assertBpm(this.baseBpm);
    this.bpm = this.baseBpm;
    this.swing = options.swing ?? 0;
    assertProbability('swing', this.swing);
    this.seed = options.seed ?? 0;
    assertSeed(this.seed);
    this.globalProbability = options.globalProbability ?? 1;
    assertProbability('globalProbability', this.globalProbability);
    this.initialPlaying = options.playing ?? false;
    this.playing = this.initialPlaying;
    this.sourceEvents = Object.freeze(options.events.map((event) => Object.freeze({ ...event })));
    this.validateEvents();
    this.prepareEvents();
  }

  reset(): void {
    this.frame = 0;
    this.tick = 0;
    this.bpm = this.baseBpm;
    this.playing = this.initialPlaying;
    this.currentValue = 0;
    this.activeEnds.length = 0;
    this.commandIndex = 0;
  }

  setTempo(bpm: number): void {
    assertBpm(bpm);
    this.baseBpm = bpm;
    this.bpm = bpm;
  }

  setSwing(swing: number): void {
    assertProbability('swing', swing);
    this.swing = swing;
    this.prepareEvents();
  }

  setGlobalProbability(probability: number): void {
    assertProbability('globalProbability', probability);
    this.globalProbability = probability;
  }

  setSeed(seed: number): void {
    assertSeed(seed);
    this.seed = seed;
    this.prepareEvents();
  }

  queueTransport(command: TransportCommand): void {
    assertTick('command frame', command.frame);
    if (command.frame < this.frame) throw new RangeError('command frame is before the current transport frame');
    if (command.type === 'tempo') assertBpm(command.bpm);
    if (command.type === 'seek') assertTick('seek tick', command.tick);
    if (command.type === 'start' && command.tick !== undefined) assertTick('start tick', command.tick);
    this.commands.push(Object.freeze({ command: Object.freeze({ ...command }), order: this.nextCommandOrder++ }));
    this.commands.sort((left, right) => left.command.frame - right.command.frame || left.order - right.order);
  }

  processInto(outputs: SequencerOutputs, frames = outputs.trigger.length): void {
    if (!Number.isInteger(frames) || frames < 0) throw new RangeError('frames must be a non-negative integer');
    for (const buffer of [outputs.trigger, outputs.gate, outputs.value, outputs.choke]) {
      if (!(buffer instanceof Float32Array) || buffer.length < frames) throw new RangeError('output buffer is missing or too short');
    }
    if (this.requiresEventCapacityPreflight(frames)) this.assertEventCapacity(frames);
    for (let index = 0; index < frames; index += 1) {
      outputs.trigger[index] = 0;
      outputs.choke[index] = 0;
      this.applyCommandsAtCurrentFrame();
      this.removeEndedEvents();
      if (this.playing) {
        const nextTick = this.tick + this.ticksPerSample();
        for (const event of this.preparedEvents) {
          let loopIndex = Math.ceil((this.tick - event.swungTick - EPSILON) / this.loopTicks);
          let occurrence = loopIndex * this.loopTicks + event.swungTick;
          while (occurrence < nextTick - EPSILON) {
            if (occurrence >= this.tick - EPSILON && this.eventPasses(event, loopIndex)) {
              outputs.trigger[index] = 1;
              outputs.choke[index] = event.chokeGroup === undefined ? 0 : event.chokeGroup + 1;
              this.currentValue = event.value;
              this.activeEnds.push(occurrence + event.durationTicks);
            }
            loopIndex += 1;
            occurrence += this.loopTicks;
          }
        }
        outputs.gate[index] = this.activeEnds.length > 0 ? 1 : 0;
        outputs.value[index] = this.currentValue;
        this.tick = nextTick;
      } else {
        outputs.gate[index] = 0;
        outputs.value[index] = this.currentValue;
      }
      this.frame += 1;
    }
  }

  private validateEvents(): void {
    const ids = new Set<string>();
    for (const event of this.sourceEvents) {
      if (!EVENT_ID.test(event.id)) throw new RangeError(`event id is invalid: ${event.id}`);
      if (ids.has(event.id)) throw new RangeError(`duplicate event id: ${event.id}`);
      ids.add(event.id);
      assertTick(`${event.id}.tick`, event.tick);
      if (event.tick >= this.loopTicks) throw new RangeError(`${event.id}.tick must be inside the loop`);
      assertIntegerRange(`${event.id}.durationTicks`, event.durationTicks, 1, Number.MAX_SAFE_INTEGER);
      if (!Number.isFinite(event.value)) throw new RangeError(`${event.id}.value must be finite`);
      assertProbability(`${event.id}.probability`, event.probability ?? 1);
      if (event.chokeGroup !== undefined) assertIntegerRange(`${event.id}.chokeGroup`, event.chokeGroup, 0, 254);
    }
  }

  private prepareEvents(): void {
    const eighth = this.ppq / 2;
    const delay = eighth * 0.5 * this.swing;
    const seedTree = new SeedTree(this.seed);
    this.preparedEvents = Object.freeze(this.sourceEvents.map((event): PreparedEvent => {
      const eighthIndex = Number.isInteger(event.tick / eighth) ? Math.round(event.tick / eighth) : -1;
      const swungTick = eighthIndex >= 0 && eighthIndex % 2 === 1 ? event.tick + delay : event.tick;
      if (!(swungTick < this.loopTicks)) throw new RangeError(`${event.id}.tick swing exceeds the loop`);
      return Object.freeze({
        ...event,
        probability: event.probability ?? 1,
        probabilitySeed: seedTree.derive(`mod-018/${event.id}`),
        swungTick,
      });
    }).sort((left, right) => left.swungTick - right.swungTick || left.id.localeCompare(right.id)));
  }

  private ticksPerSample(): number {
    return this.bpm * this.ppq / (60 * this.sampleRate);
  }

  private eventPasses(event: PreparedEvent, loopIndex: number): boolean {
    const probability = event.probability * this.globalProbability;
    if (probability <= 0) return false;
    if (probability >= 1) return true;
    const low = loopIndex >>> 0;
    const high = Math.floor(loopIndex / UINT32_RANGE) >>> 0;
    let value = (event.probabilitySeed ^ low ^ Math.imul(high, 0x9e37_79b1)) >>> 0;
    value = Math.imul(value ^ (value >>> 16), 0x21f0_aaad) >>> 0;
    value = Math.imul(value ^ (value >>> 15), 0x735a_2d97) >>> 0;
    value = (value ^ (value >>> 15)) >>> 0;
    return value / UINT32_RANGE < probability;
  }

  private requiresEventCapacityPreflight(frames: number): boolean {
    if (frames === 0 || this.preparedEvents.length === 0) return false;
    const blockEndFrame = this.frame + frames;
    if (this.commandIndex < this.commands.length && this.commands[this.commandIndex]!.command.frame < blockEndFrame) return true;
    if (!this.playing) return false;
    const tickSpan = frames * this.ticksPerSample();
    const maximumOccurrencesPerEvent = Math.ceil(tickSpan / this.loopTicks) + 1;
    return this.preparedEvents.length * maximumOccurrencesPerEvent > this.maxEventsPerBlock;
  }

  private assertEventCapacity(frames: number): void {
    let frame = this.frame;
    let tick = this.tick;
    let bpm = this.bpm;
    let playing = this.playing;
    let commandIndex = this.commandIndex;
    let acceptedEvents = 0;

    for (let index = 0; index < frames; index += 1) {
      while (commandIndex < this.commands.length && this.commands[commandIndex]!.command.frame === frame) {
        const command = this.commands[commandIndex++]!.command;
        if (command.type === 'stop') {
          playing = false;
        } else if (command.type === 'seek') {
          tick = command.tick;
        } else if (command.type === 'start') {
          if (command.tick !== undefined) tick = command.tick;
          playing = true;
        } else {
          bpm = command.bpm;
        }
      }

      if (playing) {
        const nextTick = tick + bpm * this.ppq / (60 * this.sampleRate);
        for (const event of this.preparedEvents) {
          let loopIndex = Math.ceil((tick - event.swungTick - EPSILON) / this.loopTicks);
          let occurrence = loopIndex * this.loopTicks + event.swungTick;
          while (occurrence < nextTick - EPSILON) {
            if (occurrence >= tick - EPSILON && this.eventPasses(event, loopIndex)) {
              acceptedEvents += 1;
              if (acceptedEvents > this.maxEventsPerBlock) {
                throw new RangeError(`block exceeds ${this.maxEventsPerBlock} events`);
              }
            }
            loopIndex += 1;
            occurrence += this.loopTicks;
          }
        }
        tick = nextTick;
      }
      frame += 1;
    }
  }

  private removeEndedEvents(): void {
    let write = 0;
    for (let read = 0; read < this.activeEnds.length; read += 1) {
      const end = this.activeEnds[read]!;
      if (end > this.tick + EPSILON) this.activeEnds[write++] = end;
    }
    this.activeEnds.length = write;
  }

  private applyCommandsAtCurrentFrame(): void {
    while (this.commandIndex < this.commands.length && this.commands[this.commandIndex]!.command.frame === this.frame) {
      const command = this.commands[this.commandIndex++]!.command;
      if (command.type === 'stop') {
        this.playing = false;
        this.activeEnds.length = 0;
      } else if (command.type === 'seek') {
        this.tick = command.tick;
        this.activeEnds.length = 0;
      } else if (command.type === 'start') {
        if (command.tick !== undefined) this.tick = command.tick;
        this.playing = true;
      } else {
        this.bpm = command.bpm;
      }
    }
  }
}

class EventSequencerModuleInstance implements DspModuleInstance {
  private readonly sequencer: EventSequencer;

  constructor(sampleRate: number, private readonly maxBlockSize: number) {
    this.sequencer = new EventSequencer({
      sampleRate,
      bpm: 120,
      meter: { numerator: 4, denominator: 4 },
      ppq: 960,
      loopBars: 1,
      playing: true,
      events: [0, 1, 2, 3].map((beat) => ({
        id: `beat-${beat + 1}`,
        tick: beat * 960,
        durationTicks: 120,
        value: beat,
      })),
    });
  }

  reset(): void { this.sequencer.reset(); }

  setParameter(id: string, value: number): void {
    if (id === 'bpm') { this.sequencer.setTempo(value); return; }
    if (id === 'swing') { this.sequencer.setSwing(value); return; }
    if (id === 'probability') { this.sequencer.setGlobalProbability(value); return; }
    if (id === 'seed') { this.sequencer.setSeed(value); return; }
    throw new RangeError(`unknown parameter: ${id}`);
  }

  process(block: DspProcessBlock): void {
    if (!Number.isInteger(block.frames) || block.frames < 0 || block.frames > this.maxBlockSize) {
      throw new RangeError('frames exceeds the configured maximum block size');
    }
    const trigger = block.outputs.trigger;
    const gate = block.outputs.gate;
    const value = block.outputs.value;
    const choke = block.outputs.choke;
    if (!trigger || !gate || !value || !choke) throw new RangeError('sequencer output buffer is missing');
    this.sequencer.processInto({ trigger, gate, value, choke }, block.frames);
  }
}

export const mod018 = defineDspModule({
  descriptor: {
    id: 'mir.mod-018',
    name: 'Event sequencer / transport',
    version: '0.1.0',
    inputs: [],
    outputs: [
      { id: 'trigger', kind: 'control', channels: 1 },
      { id: 'gate', kind: 'control', channels: 1 },
      { id: 'value', kind: 'control', channels: 1 },
      { id: 'choke', kind: 'control', channels: 1 },
    ],
    parameters: [
      { id: 'bpm', defaultValue: 120, minValue: 1, maxValue: 1_000, unit: 'bpm' },
      { id: 'swing', defaultValue: 0, minValue: 0, maxValue: 1, unit: 'ratio' },
      { id: 'probability', defaultValue: 1, minValue: 0, maxValue: 1, unit: 'ratio' },
      { id: 'seed', defaultValue: 0, minValue: 0, maxValue: UINT32_MAX, unit: 'uint32' },
    ],
  },
  createInstance(context) {
    if (!Number.isFinite(context.sampleRate) || context.sampleRate <= 0 || !Number.isInteger(context.maxBlockSize) || context.maxBlockSize < 1) {
      throw new RangeError('invalid DSP instance context');
    }
    return new EventSequencerModuleInstance(context.sampleRate, context.maxBlockSize);
  },
});
